'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { ConfirmationDialog, ListPageLayout } from '@terra/ui-design-system';
import { DEFAULT_LIFECYCLE_STATE_FILTER } from '@terra/shared-dto-types';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import { useAgentExecutionSSE } from '../../hooks/useAgentExecutionSSE';
import { useArchivedLifecycleFilter } from '../../hooks/useArchivedLifecycleFilter';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import {
  type OpenMainContentTabInput,
  useMainContentTabs,
} from '../../store/layoutStore';
import { useTargetId } from '../../store/projectStore';
import { useTabScopedAgentExecutionsFilters } from '../../store/tab/agentExecutionsTabScopedFilterStore';
import { AgentExecutionsBulkActionsBar } from './agent-executions-header/AgentExecutionsBulkActionsBar';
import { AgentExecutionsContextNavigation } from './agent-executions-header/AgentExecutionsContextNavigation';
import { AgentExecutionsFilterPanel } from './agent-executions-header/AgentExecutionsFilterPanel';
import { AgentExecutionsPageHeader } from './agent-executions-header/AgentExecutionsPageHeader';
import { AgentExecutionsTable } from './agent-executions-table/AgentExecutionsTable';
import {
  AgentExecutionsTableContextProvider,
  type AgentExecutionsTableContextValue,
} from './agent-executions-table/AgentExecutionsTableContext';
import { hasAnyAgentExecutionFilters } from './agent-executions-table/agent-executions-list.model';
import { useAgentExecutionsActions } from './agent-executions-table/useAgentExecutionsActions';
import { useAgentExecutionsListData } from './agent-executions-table/useAgentExecutionsListData';
import { useAgentExecutionsSelection } from './agent-executions-table/useAgentExecutionsSelection';
import { resolveExecutionsSelectAllState } from './executions-table/executions-list.model';

const SSE_REFRESH_DEBOUNCE_MS = 2_000;

export function AgentExecutionsContent() {
  const tAgent = useTranslations('agentExecutions');
  const tCommon = useTranslations('common');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const targetId = useTargetId();
  const filters = useTabScopedAgentExecutionsFilters();
  const { navigateInActiveTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const {
    lifecycleQueryParam,
    showLifecycleSelector,
    areArchivedMutationsDisabled,
  } = useArchivedLifecycleFilter({ lifecycleState: filters.lifecycleState });
  const [debouncedSearchTerm] = useDebounceValue(filters.searchTerm, 500);
  const listData = useAgentExecutionsListData({
    targetId,
    searchTerm: debouncedSearchTerm,
    statusFilter: filters.statusFilter,
    executionType: filters.executionTypeFilter,
    createdSince: filters.createdSince,
    lifecycleState: lifecycleQueryParam,
    sort: filters.sort,
  });
  const { refresh } = listData;
  const selection = useAgentExecutionsSelection({
    resetKey: [
      targetId ?? '',
      debouncedSearchTerm,
      filters.statusFilter.join(','),
      filters.executionTypeFilter ?? '',
      filters.createdSince?.toISOString() ?? '',
      lifecycleQueryParam ?? '',
    ].join('-'),
    totalCount: listData.totalCount,
    fetchAll: listData.fetchAll,
  });
  const actions = useAgentExecutionsActions({
    targetId,
    getSelectedExecutions: selection.getSelectedExecutions,
    onComplete: () => void refresh(),
    onSelectionCleared: selection.clearSelection,
  });
  const debouncedRefresh = useDebounceCallback(
    () => void refresh(),
    SSE_REFRESH_DEBOUNCE_MS,
  );

  useAgentExecutionSSE({
    targetId: targetId ?? undefined,
    enabled: Boolean(targetId),
    onTraceEvent: debouncedRefresh,
  });

  const getTabData = useCallback(
    (execution: AgentExecutionDto) =>
      ({
        id: `agent-execution-${execution.id}`,
        title: execution.objective
          ? tAgent('executionTabTitle', { objective: execution.objective })
          : tAgent('executionTabUntitled'),
        type: 'agent-execution',
        data: { executionId: execution.id },
      }) satisfies OpenMainContentTabInput,
    [tAgent],
  );
  const openExecutionContextMenu = useCallback(
    ({
      event,
      execution,
    }: {
      event: MouseEvent;
      execution: AgentExecutionDto;
    }) =>
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getTabData(execution),
          },
        ],
      }),
    [getTabData, openContextMenu],
  );
  const isSelectionLocked = actions.isBusy;
  const { selectAll } = selection;
  const { abortingIds, abortExecution, requestDelete } = actions;
  const tableContextValue = useMemo(
    (): AgentExecutionsTableContextValue => ({
      selectAllState: resolveExecutionsSelectAllState({
        areAllSelected: selection.areAllSelected,
        selectedCount: selection.selectedCount,
      }),
      hasRows: listData.executions.length > 0,
      isSelectionLocked,
      isExecutionSelected: selection.isExecutionSelected,
      onToggleExecutionSelected: selection.toggleExecution,
      onSelectAll: (selected) => {
        if (!isSelectionLocked) {
          selectAll(selected);
        }
      },
      abortingIds,
      onAbortExecution: (execution) => void abortExecution(execution),
      onDeleteExecution: requestDelete,
    }),
    [
      abortExecution,
      abortingIds,
      isSelectionLocked,
      listData.executions.length,
      requestDelete,
      selectAll,
      selection.areAllSelected,
      selection.isExecutionSelected,
      selection.selectedCount,
      selection.toggleExecution,
    ],
  );
  const hasFilters = hasAnyAgentExecutionFilters({
    searchTerm: filters.searchTerm,
    statusFilter: filters.statusFilter,
    executionType: filters.executionTypeFilter,
    createdSince: filters.createdSince,
    selectableLifecycleState: showLifecycleSelector
      ? filters.lifecycleState
      : null,
  });
  const clearFilters = () => {
    filters.setSearchTerm('');
    filters.setStatusFilter([]);
    filters.setExecutionTypeFilter(null);
    filters.setCreatedSince(null);
    filters.setLifecycleState(DEFAULT_LIFECYCLE_STATE_FILTER);
  };

  const isBulkDelete = actions.pendingDeleteCount > 1;

  return (
    <AgentExecutionsTableContextProvider value={tableContextValue}>
      <ListPageLayout
        header={<AgentExecutionsPageHeader />}
        navigation={
          <AgentExecutionsContextNavigation
            statusFilter={filters.statusFilter}
            onStatusFilterChange={filters.setStatusFilter}
            statusCounts={listData.statusCounts}
            searchTerm={filters.searchTerm}
            onSearchTermChange={filters.setSearchTerm}
            onRefresh={() => void refresh()}
            isLoading={listData.isRefreshing}
          />
        }
        filters={
          <AgentExecutionsFilterPanel
            statusFilter={filters.statusFilter}
            onStatusFilterChange={filters.setStatusFilter}
            executionType={filters.executionTypeFilter}
            onExecutionTypeChange={filters.setExecutionTypeFilter}
            createdSince={filters.createdSince}
            onCreatedSinceClear={() => filters.setCreatedSince(null)}
            isLifecycleEnabled={showLifecycleSelector}
            lifecycleState={filters.lifecycleState}
            onLifecycleStateChange={filters.setLifecycleState}
            hasActiveFilters={hasFilters}
            onClear={clearFilters}
            filteredCount={listData.totalCount}
            totalCount={listData.unfilteredTotalCount}
            selection={
              selection.selectedCount > 0 ? (
                <AgentExecutionsBulkActionsBar
                  selectedCount={selection.selectedCount}
                  abortableCount={selection.abortableCount}
                  isBulkAborting={actions.isBulkAborting}
                  isBulkDeleting={actions.isDeleting}
                  isBusy={isSelectionLocked}
                  mutationsDisabledTooltip={
                    areArchivedMutationsDisabled
                      ? tLifecycle('archivedExecutionActionTooltip')
                      : null
                  }
                  onClearSelection={selection.clearSelection}
                  onAbort={() => void actions.bulkAbort()}
                  onDelete={() => void actions.requestBulkDelete()}
                />
              ) : null
            }
          />
        }
      >
        <AgentExecutionsTable
          executions={listData.executions}
          sort={filters.sort}
          isLoading={listData.isInitialLoading}
          isFetchingMore={listData.isFetchingMore}
          hasMore={listData.hasNextPage}
          hasActiveFilters={hasFilters}
          onLoadMore={listData.loadMore}
          onSortChange={filters.setSort}
          onOpenExecution={(execution) =>
            navigateInActiveTab(getTabData(execution))
          }
          onOpenExecutionContextMenu={openExecutionContextMenu}
        />

        <ConfirmationDialog
          isOpen={actions.pendingDeleteCount > 0}
          onOpenChange={({ open }) => {
            if (!open) {
              actions.cancelDelete();
            }
          }}
          onConfirm={() => void actions.confirmDelete()}
          loading={actions.isDeleting}
          variant="destructive"
          title={tAgent('deleteDialogTitle')}
          description={
            isBulkDelete
              ? tAgent('bulkDeleteDialogDescription', {
                  count: actions.pendingDeleteCount,
                })
              : tAgent('deleteDialogDescription')
          }
          confirmText={tCommon('delete')}
          cancelText={tCommon('cancel')}
        />
      </ListPageLayout>
    </AgentExecutionsTableContextProvider>
  );
}
